import { Link } from "react-router-dom";
import { Check, MessageCircle, Phone, CalendarClock, ClipboardList, ReceiptText, Clock } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { useSEO } from "@/components/site/SEO";
import { SITE, whatsappLink, telLink } from "@/config/site";
import { track } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingCTA } from "@/components/site/FloatingCTA";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { BusinessContractForm } from "@/sections/BusinessContractForm";

const FEATURES = [
  { icon: CalendarClock, title: "Daily, weekly or customised schedules", text: "Cleaning that fits how your premises actually run." },
  { icon: ClipboardList, title: "Dedicated cleaning checklist", text: "An agreed checklist so standards stay consistent every visit." },
  { icon: ReceiptText, title: "Contract pricing & invoices", text: "Pricing based on frequency, size and workload. Invoices available on request." },
  { icon: Clock, title: "Flexible availability", text: "Morning, evening or weekend cleaning, subject to availability." },
];

const slug = "business-cleaning-contracts-dublin";

export default function BusinessContractsPage() {
  useLenis();
  useSEO({
    title: "Business Cleaning Contracts Dublin | GoCleaning Dublin",
    description: "Recurring business cleaning contracts in Dublin. Daily, weekly or customised schedules for offices and commercial premises, with contract pricing and invoices on request.",
    canonical: `${SITE.url}/${slug}`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Service", serviceType: "Business Cleaning Contracts",
      name: "Business Cleaning Contracts Dublin",
      description: "Recurring cleaning contracts for offices and commercial premises across Dublin and Kildare.",
      areaServed: ["Dublin", "County Kildare"], url: `${SITE.url}/${slug}`,
      provider: { "@type": "CleaningService", name: SITE.name, telephone: SITE.phoneHref, email: SITE.email, url: SITE.url },
    },
  });

  return (
    <div className="App bg-white">
      <Header />
      <main>
        <section className="bg-white pt-32 pb-12 sm:pt-36" data-testid="contracts-hero">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <nav className="mb-4 flex items-center gap-1.5 text-sm text-slate-400" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-emerald-700">Home</Link><span>/</span>
                <Link to="/services" className="hover:text-emerald-700">Services</Link><span>/</span>
                <span className="text-slate-600">Business Cleaning Contracts</span>
              </nav>
              <h1 className="font-display text-5xl font-black tracking-tighter text-slate-900 sm:text-6xl">Business Cleaning Contracts Dublin</h1>
              <p className="mt-5 max-w-2xl text-lg text-slate-600">Recurring cleaning for companies across Dublin and Kildare. We set up daily, weekly or customised cleaning schedules for offices and commercial premises, with clear contract pricing and a dedicated checklist.</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#contract-form" className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 px-7 py-4 font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-800">Request a Contract Quote</a>
                <a href={telLink()} onClick={track.phoneClick} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-4 font-bold text-slate-800 hover:border-emerald-600 hover:text-emerald-700"><Phone size={18} /> Call {SITE.phoneDisplay}</a>
                <a href={whatsappLink("Hello, I'd like to discuss a business cleaning contract.")} onClick={() => track.whatsappClick("contracts")} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 font-bold text-white hover:scale-105 transition-transform"><MessageCircle size={18} /> WhatsApp</a>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="bg-slate-50 py-16" data-testid="contracts-features">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal><h2 className="font-display text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">How our cleaning contracts work</h2></Reveal>
            <Stagger className="mt-8 grid gap-5 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <StaggerItem key={f.title}>
                  <div className="flex h-full items-start gap-4 rounded-3xl border border-slate-200/70 bg-white p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-700 text-white"><f.icon size={20} /></span>
                    <div><h3 className="font-display text-lg font-bold text-slate-900">{f.title}</h3><p className="mt-1 text-slate-600">{f.text}</p></div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
            <Reveal>
              <ul className="mt-8 grid gap-2 sm:grid-cols-2">
                {["Office and commercial premises", "Site visit or photos before final quote", "One-off, weekly or scheduled contracts", "Customised cleaning plans for your business"].map((i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700"><Check size={17} className="text-emerald-600" /> {i}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section className="bg-emerald-50 py-16 sm:py-24" data-testid="contracts-form-section">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">Business enquiry</p>
              <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">Request a Contract Quote</h2>
              <p className="mt-4 text-lg text-slate-600">Tell us about your premises and cleaning needs and we'll arrange a site visit or quote.</p>
            </div>
            <BusinessContractForm />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}
